import { Component, ElementRef, EventEmitter, Output, ViewChild } from '@angular/core';
import { CustomModalService } from 'libs/shared/src/lib/modal/custom-modal.service';

export type MultiSelectItemId = string | number;

export interface MultiSelectItem {
  id: MultiSelectItemId;
  name: string;
  unit?: string;
  [key: string]: any;
}

@Component({
  selector: 'piqet-angular-indicator-select-modal',
  templateUrl: './indicator-select-modal.component.html',
  styleUrls: ['./indicator-select-modal.component.scss'],
})
export class IndicatorSelectModalComponent {
  @Output() selectionConfirmed = new EventEmitter<MultiSelectItemId[]>();
  @ViewChild('indicatorsList') indicatorsList?: ElementRef<HTMLElement>;

  items: MultiSelectItem[] = [];
  selectedIds: MultiSelectItemId[] = [];
  maxSelection: number | null = null;
  title = 'Categories to include';
  itemNoun = 'categories';
  highlightId: MultiSelectItemId | null = null;
  highlightLabel = '';

  searchTerm = '';
  private selection = new Set<string>();

  constructor(private modal: CustomModalService) {}

  ngOnInit(): void {
    this.selection = new Set((this.selectedIds ?? []).map(id => String(id)));
  }

  get filteredItems(): MultiSelectItem[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) return this.items;
    return this.items.filter(
      x =>
        x.name?.toLowerCase().includes(term) ||
        (x.unit ? x.unit.toLowerCase().includes(term) : false)
    );
  }

  get selectedCount(): number {
    return this.selection.size;
  }

  get limitReached(): boolean {
    return this.maxSelection != null && this.selection.size >= this.maxSelection;
  }

  get limitLabel(): string {
    return this.maxSelection != null
      ? `${this.selection.size} of ${this.maxSelection} selected`
      : `${this.selection.size} selected`;
  }

  get allFilteredSelected(): boolean {
    const filtered = this.filteredItems;
    return filtered.length > 0 && filtered.every(x => this.selection.has(String(x.id)));
  }

  isSelected(item: MultiSelectItem): boolean {
    return this.selection.has(String(item.id));
  }

  isDisabled(item: MultiSelectItem): boolean {
    return !this.isSelected(item) && this.limitReached;
  }

  isHighlighted(item: MultiSelectItem): boolean {
    return this.highlightId != null && String(this.highlightId) === String(item.id);
  }

  toggle(item: MultiSelectItem): void {
    const key = String(item.id);
    if (this.selection.has(key)) {
      this.selection.delete(key);
      return;
    }
    if (this.limitReached) return;
    this.selection.add(key);
  }

  toggleAllFiltered(): void {
    const filtered = this.filteredItems;
    if (this.allFilteredSelected) {
      filtered.forEach(x => this.selection.delete(String(x.id)));
      return;
    }
    for (const item of filtered) {
      if (this.limitReached) break;
      this.selection.add(String(item.id));
    }
  }

  clearAll(): void {
    this.selection.clear();
    this.scrollListToTop();
  }

  private scrollListToTop(): void {
    if (this.indicatorsList) {
      this.indicatorsList.nativeElement.scrollTop = 0;
    }
  }

  apply(): void {
    const ordered = this.items.map(x => x.id).filter(id => this.selection.has(String(id)));
    this.selectionConfirmed.emit(ordered);
    this.modal.close();
  }

  onClose(): void {
    this.modal.close();
  }
}
